import { useUnit } from 'effector-react';
import { CircleSlash2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { AccountWithWalletsAndPermissions } from 'shared/api';
import {
	ConfirmModalProps,
	createConfirmModal,
	ModalFormActionIconProps,
	ModalFormButtonProps,
} from 'shared/ui';
import { BlockAccountModel } from '../../model';

export const createBlockAccountModal = (model: BlockAccountModel) => {
	const ConfirmModal = createConfirmModal(model);

	const BlockAccountModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [account] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('block-account.title')}
				cancelButtonProps={{
					children: t('block-account.cancel'),
				}}
				submitButtonProps={{
					variant: 'outline',
					children: t('block-account.block'),
				}}
				{...props}
			>
				{t('block-account.description', { name: account?.name })}
			</ConfirmModal>
		);
	});

	const BlockAccountModalButton = forwardRef<
		HTMLButtonElement,
		ModalFormButtonProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const { t } = useTranslation();

		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ConfirmModal.Button
				ref={ref}
				data-test-id='block-account-button'
				variant='outline'
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				leftSection={<CircleSlash2 />}
				modalData={modalData}
				{...props}
			>
				{t('block-account.block')}
			</ConfirmModal.Button>
		);
	});

	const BlockAccountModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ConfirmModal.ActionIcon
				ref={ref}
				data-test-id='block-account-action-icon'
				variant='ghost-secondary'
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				modalData={modalData}
				{...props}
			>
				<CircleSlash2 />
			</ConfirmModal.ActionIcon>
		);
	});

	return Object.assign(BlockAccountModal, {
		Button: BlockAccountModalButton,
		ActionIcon: BlockAccountModalActionIcon,
	});
};
